import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ToastProvider';
import { errorCode, errorMessage } from '@/lib/errors';
import { formatMoney } from '@/lib/format';
import { fromMinor } from '@/lib/money';
import {
  newOperationId,
  financeRefundsQuery,
  OPERATION_CONFLICT_MESSAGE,
  useInvalidateFinance,
  useRefundAttach,
  useSettleProviderFact,
} from '@/lib/finance';
import { FINANCE_REFUND_STATUS_TONE } from '@/lib/status-tone';
import { AttachmentList } from './AttachmentList';
import { ReceiptUploadButton } from './ReceiptUploadButton';
import { useSettleForm } from './useSettleForm';
import type { FinanceProviderFact, FinanceRefund, FinanceRefundAttempt } from '@/api/generated/types.gen';

export interface AttachToObligationDialogProps {
  attempt: FinanceRefundAttempt | null;
  onClose: () => void;
}

/**
 * "Attach to obligation" (fix round 1, item 1 — Critical, Ruling T23-1): the
 * operator picks an EXISTING refund obligation of the SAME payment
 * (listFinanceRefunds({payment_id})) rather than typing an id — before this
 * fix, no screen anywhere ever displayed a refund_id value, so the old
 * "type the obligation's id" prompt was unusable by any operator, in any
 * circumstance (review's Critical finding).
 */
export function AttachToObligationDialog({ attempt, onClose }: AttachToObligationDialogProps) {
  return (
    <Dialog open={!!attempt} onClose={onClose} title="Attach to obligation" description={attempt ? `Attempt ${formatMoney(attempt.amount_minor, attempt.currency)}` : undefined}>
      {attempt && <AttachToObligationForm key={attempt.id} attempt={attempt} onClose={onClose} />}
    </Dialog>
  );
}

// D-M3 (final review): the server (AttachRefundTx, internal/payment/refunds.go)
// only accepts an obligation that is post-open (open, uncovered) or
// written-off-and-uncovered, of the EXACT SAME amount/currency as the
// attempt — everything else 422s with "split first" or a precondition
// error. Mirrors the Go predicate: `r.IsPostOpen() || (status===written_off
// && !covered_by)`, `is_post_open` being the server's own
// `refund_is_target`-adjacent computed field (never re-derived from status
// here — it already encodes the covered_by check).
function isAttachCandidate(refund: FinanceRefund, attempt: FinanceRefundAttempt): boolean {
  const eligibleStatus = refund.is_post_open || (refund.status === 'written_off' && !refund.covered_by);
  return eligibleStatus && refund.amount_minor === attempt.amount_minor && refund.currency === attempt.currency;
}

function AttachToObligationForm({ attempt, onClose }: { attempt: FinanceRefundAttempt; onClose: () => void }) {
  const { data, isLoading } = useQuery(financeRefundsQuery({ paymentId: attempt.payment_id }));
  const [refundId, setRefundId] = useState('');
  const [operationId] = useState(newOperationId);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const attach = useRefundAttach();
  // D-M4 (final review): same conflict handling as ConfirmReceiptDialog —
  // this form mints one operation_id for its own life (no formKey/retry
  // dance needed, the dialog remounts per attempt via key={attempt.id}),
  // but a lost-response-then-edited-retry still hits 409 operation_conflict.
  const invalidate = useInvalidateFinance();
  const items = (data?.items ?? []).filter((it) => isAttachCandidate(it.refund, attempt));

  function submit() {
    if (!refundId) return setError('Pick which obligation this attaches to.');
    setError(null);
    attach.mutate(
      { refundId, attemptId: attempt.id, operationId },
      {
        onSuccess: () => {
          toast({ kind: 'success', title: 'Attached' });
          onClose();
        },
        onError: (err) => {
          if (errorCode(err) === 'operation_conflict') {
            invalidate();
            setError(OPERATION_CONFLICT_MESSAGE);
          } else {
            setError(errorMessage(err));
          }
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {isLoading ? (
        <p className="text-sm text-muted">Loading obligations…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted">No refund obligations on this payment.</p>
      ) : (
        <Select
          label="Obligation"
          value={refundId}
          placeholder="Pick an obligation"
          options={items.map((it) => ({
            value: it.refund.id,
            label: `${formatMoney(it.refund.amount_minor, it.refund.currency)} — ${FINANCE_REFUND_STATUS_TONE[it.refund.status].label}`,
          }))}
          onChange={(e) => setRefundId(e.target.value)}
        />
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={attach.isPending}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} loading={attach.isPending} disabled={items.length === 0}>
          Attach
        </Button>
      </div>
    </div>
  );
}

export interface SettleProviderFactDialogProps {
  fact: FinanceProviderFact | null;
  businessTz?: string;
  onClose: () => void;
}

/**
 * Settle a third-source provider fact directly from the events screen (spec
 * §9 — the fact never got a matching schedule row, or the operator wants to
 * settle it here rather than from the booking card). Same equation as
 * ConfirmReceiptDialog's own projection branch: the settle amount is FIXED
 * at the fact's own magnitude (psp_fact_indivisible otherwise, server-side)
 * — the only free variable is the bank amount, and any shortfall becomes a
 * withheld line against a fee article. To an account in another currency
 * the operator types that withheld fee in the account currency instead
 * (Ruling FR-28 — the server converts bank + fee and checks it against the
 * fact). Every provider fact is a projection
 * (there is no "plain row" case here, unlike a schedule entry), so
 * useSettleForm is always called with isProjection: true.
 *
 * The form's own state/equation/validation lives in useSettleForm.ts,
 * shared verbatim with ConfirmReceiptDialog.tsx (fix round 1, item 4: these
 * two used to duplicate the whole block).
 */
export function SettleProviderFactDialog({ fact, businessTz, onClose }: SettleProviderFactDialogProps) {
  return (
    <Dialog
      open={!!fact}
      onClose={onClose}
      side
      title="Settle"
      description={fact ? `${fact.kind} · ${formatMoney(fact.amount_minor, fact.currency)}` : undefined}
    >
      {fact && <SettleProviderFactForm key={fact.id} fact={fact} businessTz={businessTz} onClose={onClose} />}
    </Dialog>
  );
}

function SettleProviderFactForm({
  fact,
  businessTz,
  onClose,
}: {
  fact: FinanceProviderFact;
  businessTz?: string;
  onClose: () => void;
}) {
  const planMinor = Math.abs(fact.amount_minor);
  const form = useSettleForm({ isProjection: true, planMinor, currency: fact.currency, businessTz });
  const { toast } = useToast();
  const settle = useSettleProviderFact();
  // D-M10 (final review, docs/30 §15's own promise): the provider fee, when
  // the fact carries one, is "substituted into the settle form as a
  // ready-made article" — the bank amount defaults to plan minus the fee
  // (so the derived withheld equals provider_fee_minor exactly) and the fee
  // category defaults to paypal_fee, the only PSP currently integrated
  // (CLAUDE.md — PayPal is the current payment provider; provider_fee_minor
  // only ever comes from a PayPal-sourced payments/payment_refunds row).
  // Guarded by a ref, not a dependency array: form.feeCategories is a new
  // array every render (useSettleForm's own .filter()), so a dependency
  // array keyed on it would refire every render; the ref makes this a true
  // one-time prefill that the operator can still freely edit afterwards.
  const providerFeePrefilledRef = useRef(false);
  //
  // Ruling FR-29 (b): plan − fee is an amount in the FACT's currency, so it
  // is only a bank amount when the account is in that currency too. The
  // prefill therefore waits for the default account (and with it the
  // payment currency) to resolve — accounts load asynchronously, and before
  // that form.currency is merely the fact's own — and, for an account in
  // another currency, pre-selects the article only: the operator types the
  // bank amount and the account-currency fee (FR-28) from the statement.
  useEffect(() => {
    if (providerFeePrefilledRef.current) return;
    if (!fact.provider_fee_minor || fact.provider_fee_minor <= 0) return;
    const feeCategory = form.feeCategories.find((c) => c.code === 'paypal_fee');
    if (!feeCategory) return; // categories not loaded yet — try again next render
    if (!form.accountId) return; // payment currency not resolved yet — try again next render
    providerFeePrefilledRef.current = true;
    form.setWithheldCategoryId(feeCategory.id);
    if (form.currency !== fact.currency) return;
    form.setBankMajor(fromMinor(Math.max(planMinor - fact.provider_fee_minor, 0), fact.currency));
  });
  // D-M4 (final review): same conflict handling as ConfirmReceiptDialog.
  const invalidate = useInvalidateFinance();

  function submit() {
    const body = form.buildBody();
    if (!body) return;
    settle.mutate(
      { kind: fact.kind, id: fact.id, body },
      {
        onSuccess: () => {
          toast({ kind: 'success', title: 'Settled' });
          onClose();
        },
        onError: (err) => {
          if (errorCode(err) === 'operation_conflict') {
            invalidate();
            form.setError(OPERATION_CONFLICT_MESSAGE);
          } else if (errorCode(err) === 'income_side_disabled') {
            form.setError('Settling provider facts turns on once the finance-block cutover runs.');
          } else {
            form.setError(errorMessage(err));
          }
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Select
        label="Account"
        value={form.accountId}
        options={form.accounts.filter((a) => a.is_active).map((a) => ({ value: a.id, label: a.name }))}
        onChange={(e) => {
          const next = form.accounts.find((a) => a.id === e.target.value);
          form.setAccountId(e.target.value);
          form.setCurrency(next?.currencies.includes(fact.currency) ? fact.currency : (next?.currencies[0] ?? fact.currency));
        }}
      />
      <Select
        label="Currency"
        value={form.currency}
        options={(form.account?.currencies ?? [fact.currency]).map((c) => ({ value: c, label: c }))}
        onChange={(e) => form.setCurrency(e.target.value)}
      />
      <Input label="Date" type="date" value={form.occurredOn} onChange={(e) => form.setOccurredOn(e.target.value)} required />
      <MoneyInput label="Bank amount" currency={form.currency || 'EUR'} value={form.bankMajor} onChange={form.setBankMajor} required />
      {form.withheldEntered && (
        <MoneyInput
          label="Withheld fee"
          currency={form.currency || 'EUR'}
          value={form.withheldMajor}
          onChange={form.setWithheldMajor}
          hint={`In ${form.currency}, as on the statement — the server converts it at the rate for the date and checks it against the ${fact.currency} amount`}
        />
      )}
      {form.withheldMinor !== null && form.withheldMinor > 0 && (
        <Select
          label={`Withheld ${formatMoney(form.withheldMinor, form.currency)} — article`}
          value={form.withheldCategoryId}
          placeholder="Pick the fee article"
          options={form.feeCategories.map((c) => ({ value: c.id, label: c.name }))}
          onChange={(e) => form.setWithheldCategoryId(e.target.value)}
        />
      )}
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted">Receipts</p>
        <ReceiptUploadButton
          target={{}}
          onUploaded={(id, file) => {
            form.setUploadError(null);
            form.setReceipts((rs) => [...rs, { id, name: file.name }]);
          }}
          onError={form.setUploadError}
        />
        <AttachmentList
          className="mt-2 flex flex-col gap-1"
          items={form.receipts}
          onRemove={(id) => form.setReceipts((rs) => rs.filter((r) => r.id !== id))}
        />
        {form.uploadError && (
          <p role="alert" className="mt-1 text-sm text-destructive">
            {form.uploadError}
          </p>
        )}
      </div>
      {form.error && (
        <p role="alert" className="text-sm text-destructive">
          {form.error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={settle.isPending}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} loading={settle.isPending}>
          Confirm
        </Button>
      </div>
    </div>
  );
}
